/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2019, Zumper
 * @description  BaseLogger.ts
 */

import { getGlobalLogger, getGlobalLogLevel } from './globalLogger'
import { DEFAULT_LOG_LEVEL } from './settings'

import {
  LOG_LEVEL_CRITICAL,
  LOG_LEVEL_DEBUG,
  LOG_LEVEL_ERROR,
  LOG_LEVEL_INFO,
  LOG_LEVEL_LOG,
  LOG_LEVEL_NONE,
  LOG_LEVEL_WARN,
} from './constants'
import { LOG_LEVEL } from './types'

export enum LoggerHandlerName {
  handleDebug = 'handleDebug',
  handleLog = 'handleLog',
  handleInfo = 'handleInfo',
  handleWarn = 'handleWarn',
  handleError = 'handleError',
  handleCritical = 'handleCritical',
}

export type LoggerMethod = (...args: any) => void

export interface Logger {
  debug: LoggerMethod
  log: LoggerMethod
  info: LoggerMethod
  warn: LoggerMethod
  error: LoggerMethod
  critical: LoggerMethod
  handleDebug: LoggerMethod
  handleLog: LoggerMethod
  handleInfo: LoggerMethod
  handleWarn: LoggerMethod
  handleError: LoggerMethod
  handleCritical: LoggerMethod
}

export interface BaseOptions {
  logger?: Logger
  logLevel?: LOG_LEVEL
}

export abstract class BaseLogger {
  logger?: Logger
  logLevel: LOG_LEVEL
  constructor(options?: BaseOptions) {
    if (options?.logger !== undefined) {
      this.logger = options.logger
    }
    this.logLevel = options?.logLevel ?? DEFAULT_LOG_LEVEL
  }

  setLogger(logger: Logger): void {
    this.logger = logger
  }

  setLogLevel(level: LOG_LEVEL): void {
    this.logLevel = level
  }

  debug(...args: any): void {
    this.callHandler(args, LoggerHandlerName.handleDebug, LOG_LEVEL_DEBUG)
  }

  log(...args: any): void {
    this.callHandler(args, LoggerHandlerName.handleLog, LOG_LEVEL_LOG)
  }

  info(...args: any): void {
    this.callHandler(args, LoggerHandlerName.handleInfo, LOG_LEVEL_INFO)
  }

  warn(...args: any): void {
    this.callHandler(args, LoggerHandlerName.handleWarn, LOG_LEVEL_WARN)
  }

  error(...args: any): void {
    this.callHandler(args, LoggerHandlerName.handleError, LOG_LEVEL_ERROR)
  }

  critical(...args: any): void {
    this.callHandler(args, LoggerHandlerName.handleCritical, LOG_LEVEL_CRITICAL)
  }

  callHandler(
    args: unknown[],
    name: LoggerHandlerName,
    level: Exclude<LOG_LEVEL, typeof LOG_LEVEL_NONE>
  ): void {
    // bail for low log levels
    const globalLogLevel = getGlobalLogLevel()
    const logLevel =
      globalLogLevel !== undefined ? globalLogLevel : this.logLevel
    if (level < logLevel || logLevel === LOG_LEVEL_NONE) {
      return
    }
    // allow for overriding the logger
    const globalLogger = getGlobalLogger()
    const logger =
      globalLogger !== undefined
        ? globalLogger
        : this.logger !== undefined
          ? this.logger
          : this
    logger[name].apply(logger, args)
  }

  abstract handleDebug(...args: unknown[]): void

  abstract handleLog(...args: unknown[]): void

  abstract handleInfo(...args: unknown[]): void

  abstract handleWarn(...args: unknown[]): void

  abstract handleError(...args: unknown[]): void

  abstract handleCritical(...args: unknown[]): void
}
